/**
 * @fileoverview A plant's week as a grid: seven days across, the day and the night down,
 * and a rain row when there is rain.
 *
 * It replaced day codes in capitals ("TUE-WED, SUN-MON NIGHTS"), which a
 * gardener had to read and map onto a calendar in their head. A calendar is
 * the one chart every gardener already reads, and here night is a place on
 * it, not a word.
 *
 * Every day and night carries its reading, marked by what it means for the
 * plant, in the words of its temperature scale (the About sheet):
 *
 * - plain: where it grows best, or within a few degrees of it;
 * - a tint: it grows slowly, cool or warm;
 * - an outline: harm, a chilling night, a cold snap or a heat spike;
 * - solid with a warning mark: a danger, frost, freeze, or past what it
 *   survives.
 *
 * The rain row shows each day's rain, and only when the week has any; a
 * downpour, more in a day than the plant can take, is outlined as harm with
 * a drop in its corner. Watering is the week's plan, said once under the
 * grid rather than as a can on every day. Nothing in the grid is a button:
 * the items under it name their days.
 *
 * Colours are the theme's verdict tokens.
 *
 * Where: the card's temperature section (TemperatureForecast).
 * API: none; TemperatureForecast builds the cells.
 * Words (en/es): plants.card.grid.day, .night, .rain.
 *
 * @module WeekGrid
 */

import React from 'react';
import { AlertTriangle, CloudRain, Droplet } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { TOKEN_BORDER, TOKEN_SOLID, TOKEN_TEXT, TOKEN_TINT, type VerdictToken } from '@/shared/theme/verdictTokens';

/** How a cell is marked; see the module comment. */
export type CellFill = 'plain' | 'tint' | 'harm' | 'solid';

/** One cell: its reading, how it is marked, and what a screen reader hears. */
export interface GridCell {
  /** The reading, "88°" or "1.8″", or null for an empty cell. */
  text: string | null;
  /** Its colour: the ink of a plain cell (rain), the tint, outline or fill of the rest; null for a plain reading. */
  token: VerdictToken | null;
  /** How it is marked. */
  fill: CellFill;
  /** A drop beside the amount: a downpour. */
  drop?: boolean;
  /** Read out in full, e.g. "Wednesday night, 58°F, grows slowly". */
  label: string;
}

/** One column: the day's name and its cells, any of which may be empty. */
export interface GridDay {
  /** "YYYY-MM-DD". */
  date: string;
  /** The column's heading: "Today", "Thu". */
  name: string;
  /** The day's high. */
  day: GridCell | null;
  /** The night's low. */
  night: GridCell | null;
  /** The day's rain, drawn only in a week with rain. */
  rain: GridCell | null;
}

/** What the grid draws. */
interface WeekGridProps {
  /** The columns, today first. */
  days: GridDay[];
  /** Whether to draw the rain row: only in a week with rain. */
  showRain: boolean;
}

/** Day: a filled sun. */
const SunSymbol: React.FC = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 text-verdict-sun" aria-hidden="true">
    <g stroke="currentColor" strokeWidth={2.2} strokeLinecap="round">
      <path d="M12 1.5v3M12 19.5v3M1.5 12h3M19.5 12h3M4.6 4.6l2.1 2.1M17.3 17.3l2.1 2.1M4.6 19.4l2.1-2.1M17.3 6.7l2.1-2.1" />
    </g>
    <circle cx={12} cy={12} r={5.5} fill="currentColor" />
  </svg>
);

/** Night: a filled crescent moon with two stars. */
const MoonSymbol: React.FC = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 text-verdict-cool" aria-hidden="true">
    <path d="M20.5 14.8A9 9 0 1 1 9.2 3.5a7.2 7.2 0 0 0 11.3 11.3z" fill="currentColor" />
    <circle cx={17.5} cy={5} r={1} fill="currentColor" />
    <circle cx={20.5} cy={9} r={0.7} fill="currentColor" />
  </svg>
);

/**
 * The grid, with the row symbols down the left and a header of days.
 *
 * @param props - WeekGridProps.
 */
export const WeekGrid: React.FC<WeekGridProps> = ({ days, showRain }) => {
  const { reactTranslate: t } = useTranslation();

  /**
   * One cell, marked by its fill. Rain is set a little smaller: "1.8″" and a
   * downpour's drop have to fit a phone's narrow column.
   *
   * @param c - The cell, or null for an empty one.
   * @param key - Its React key.
   * @param small - Rain's smaller figures.
   */
  const cell = (c: GridCell | null, key: string, small = false) => {
    if (!c) return <span key={key} className="h-9 rounded-sm border border-border/60" aria-hidden="true" />;
    const look = cn(
      'relative flex h-9 items-center justify-center rounded-sm font-semibold tabular-nums',
      small ? 'text-[0.71875rem] tracking-tighter' : 'text-[0.8125rem]',
      c.token === null ? (c.fill === 'solid' ? 'bg-foreground text-background' : 'border border-border text-foreground')
        : c.fill === 'plain' ? cn('border border-border', TOKEN_TEXT[c.token])
        : c.fill === 'solid' ? TOKEN_SOLID[c.token]
          : cn(TOKEN_TINT[c.token], c.fill === 'harm' && cn('border-2', TOKEN_BORDER[c.token])),
    );
    return (
      <span key={key} className={look} role="img" aria-label={c.label}>
        {/* In the corner, as a danger's mark is: beside the amount it did not fit a phone's column. */}
        {c.drop && <Droplet className="absolute left-0.5 top-0.5 h-2 w-2 fill-current" aria-hidden="true" />}
        {c.text}
        {c.fill === 'solid' && <AlertTriangle className="absolute right-0.5 top-0.5 h-2.5 w-2.5" aria-hidden="true" />}
      </span>
    );
  };

  /** The rows, each with its symbol down the left and a name for screen readers. */
  const rows: Array<{ key: 'day' | 'night' | 'rain'; symbol: React.ReactNode; label: string }> = [
    { key: 'day', symbol: <SunSymbol />, label: t('plants.card.grid.day') },
    { key: 'night', symbol: <MoonSymbol />, label: t('plants.card.grid.night') },
    ...(showRain ? [{ key: 'rain' as const, symbol: <CloudRain className="h-5 w-5 text-verdict-water" aria-hidden="true" />, label: t('plants.card.grid.rain') }] : []),
  ];

  return (
    <div
      className="grid items-center gap-[3px] font-sans"
      style={{ gridTemplateColumns: `2rem repeat(${days.length}, minmax(0, 1fr))` }}
    >
      <span />
      {days.map((d, i) => (
        <span key={d.date} className={cn('whitespace-nowrap text-center text-[0.6875rem] font-semibold tracking-tight', i === 0 ? 'text-foreground' : 'text-muted-foreground')}>
          {d.name}
        </span>
      ))}
      {rows.map((row) => (
        <React.Fragment key={row.key}>
          <span className="flex justify-center" title={row.label}>
            {row.symbol}
            <span className="sr-only">{row.label}</span>
          </span>
          {days.map((d) => cell(d[row.key], `${row.key}-${d.date}`, row.key === 'rain'))}
        </React.Fragment>
      ))}
    </div>
  );
};
